import React, {Component, PropTypes} from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {bindActionCreators} from 'redux';
import {autobind, throttle} from 'core-decorators';
import requestAnimationFrame from 'raf';
import cubicInOut from 'eases/cubic-in-out';
import {connect} from 'store';
import {seek} from 'store/video/actions';
import {searchExactTranscriptToken} from 'store/search/actions';
import {disableAutoscroll, resumeAutoscroll} from 'store/ui/actions';
import {getElementListForTranscript} from 'store/element-list/selectors';
import AutoscrollButton from './autoscroll-button';
import Sentence from './sentence';

@autobind
export class Transcript extends Component {
    static propTypes = {
        elementList: ImmutablePropTypes.map.isRequired,
        speakersVisible: PropTypes.bool.isRequired,
        highlightTokensByTime: PropTypes.bool.isRequired,
        resultIndex: PropTypes.number.isRequired,
        rtl: PropTypes.bool.isRequired,
        fontSize: PropTypes.string,
        searchExactTranscriptToken: PropTypes.func.isRequired,
        disableAutoscroll: PropTypes.func.isRequired,
        resumeAutoscroll: PropTypes.func.isRequired,
        seek: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        this.autoscroll = true;
        this.state = {
            scrollbarWidth: 0,
        };
    }

    componentDidMount() {
        this.calculateScrollbarWidth();
    }

    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.elementList !== this.props.elementList
            || nextProps.speakersVisible !== this.props.speakersVisible
            || nextProps.highlightTokensByTime !== this.props.highlightTokensByTime
            || nextProps.resultIndex !== this.props.resultIndex
            || nextProps.rtl !== this.props.rtl
            || nextState.scrollbarWidth !== this.state.scrollbarWidth;
    }

    componentDidUpdate(prevProps) {
        const {resultIndex} = this.props;
        if (this.autoscroll) {
            this.setupAutoscroll(this.getAutoscrollSelector());
        } else if (resultIndex !== -1 && resultIndex !== prevProps.resultIndex) {
            this.setupAutoscroll('.cielo24-vwrap-token.cielo24-vwrap-highlight-active-result');
        }
        this.calculateScrollbarWidth();
    }

    setSentencesRef(ref) {
        this.sentencesNode = ref;
    }

    calculateScrollbarWidth() {
        const scrollbarWidth = this.sentencesNode.offsetWidth - this.sentencesNode.clientWidth;
        if (this.state.scrollbarWidth !== scrollbarWidth) {
            this.setState({scrollbarWidth});
        }
    }

    getAutoscrollSelector() {
        if (this.props.highlightTokensByTime) return '.cielo24-vwrap-token.cielo24-vwrap-highlight-time';
        return '.cielo24-vwrap-sentence.cielo24-vwrap-highlight-time';
    }

    getScrollTarget(targetSelector) {
        const targetNode = this.sentencesNode.querySelector(targetSelector);
        if (targetNode) {
            const {top: containerTop, height: containerHeight} = this.sentencesNode.getBoundingClientRect();
            const {top: targetTop, height: targetHeight} = targetNode.getBoundingClientRect();
            const offset = targetHeight > containerHeight ? 0 : (containerHeight - targetHeight) / 2;
            return targetTop + this.sentencesNode.scrollTop - containerTop - offset;
        }
    }

    setupAutoscroll(targetSelector, force = false) {
        const newScrollTarget = this.getScrollTarget(targetSelector);
        if (newScrollTarget !== undefined && (newScrollTarget !== this.scrollTarget || force)) {
            this.scrollTarget = newScrollTarget;
            this.scrollStart = this.sentencesNode.scrollTop;
            this.scrollDistance = this.scrollTarget - this.scrollStart;
            this.scrollDuration = Math.max(200, Math.min(800, Math.abs(this.scrollDistance / 20)));
            delete this.scrollTimeStart;
            this.scrollAnimationId = requestAnimationFrame(this.animateScroll);
        }
    }

    animateScroll(time) {
        if (!this.scrollTimeStart) this.scrollTimeStart = time;
        const elapsed = time - this.scrollTimeStart;
        const offset = cubicInOut(Math.max(0, Math.min(1, elapsed / this.scrollDuration))) * this.scrollDistance;
        this.sentencesNode.scrollTop = this.scrollStart + offset;

        if (elapsed < this.scrollDuration) {
            this.scrollAnimationId = requestAnimationFrame(this.animateScroll);
        } else {
            this.sentencesNode.scrolltop = this.scrollTarget;
            requestAnimationFrame.cancel(this.scrollAnimationId);
        }
    }

    cancelAutoscroll() {
        if (this.autoscroll) {
            this.autoscroll = false;
            this.forceUpdate();
            this.props.disableAutoscroll();
            requestAnimationFrame.cancel(this.scrollAnimationId);
        }
    }

    @throttle
    handleOnWheel() {
        this.cancelAutoscroll();
    }

    handleOnKeyUp(event) {
        // Cancel autoscroll when using keyboard arrows
        if ([38, 40].includes(event.keyCode)) this.cancelAutoscroll();
    }

    handleOnMouseDown(event) {
        if (!event.target.classList.contains('token')) this.cancelAutoscroll();
    }

    handleResumeAutoscroll() {
        if (!this.autoscroll) {
            this.autoscroll = true;
            this.setupAutoscroll(this.getAutoscrollSelector(), true);
            this.forceUpdate();
            this.props.resumeAutoscroll();
        }
    }

    handleTokenClick(token) {
        const searched = this.props.searchExactTranscriptToken(token);
        if (!searched) this.props.seek(token.get('startTime'));
    }

    render() {
        const {elementList, speakersVisible, highlightTokensByTime, rtl} = this.props;
        return (
            <div className='cielo24-vwrap-transcript'>
                <div
                    tabIndex={0}
                    className='cielo24-vwrap-sentences'
                    ref={this.setSentencesRef}
                    onWheel={this.handleOnWheel}
                    onKeyUp={this.handleOnKeyUp}
                    onTouchMove={this.handleOnWheel}
                    onMouseDown={this.handleOnMouseDown}
                >
                    {elementList.get('segments').map(segment => (
                        <Sentence
                            key={`segment-${segment.get('startTime')}`}
                            fontSize={this.props.fontSize}
                            segment={segment}
                            showSpeaker={speakersVisible}
                            showTimestamp={true}
                            showSearchResults={true}
                            highlightTokensByTime={highlightTokensByTime}
                            onClick={this.handleTokenClick}
                        />
                    ))}
                </div>
                <AutoscrollButton
                    visible={!this.autoscroll}
                    scrollbarWidth={this.state.scrollbarWidth}
                    rtl={rtl}
                    onClick={this.handleResumeAutoscroll}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    elementList: getElementListForTranscript(state),
    speakersVisible: state.config.get('speakersVisible'),
    highlightTokensByTime: state.config.get('wordHighlight'),
    resultIndex: state.search.get('resultIndex'),
    rtl: state.config.get('rtl'),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    searchExactTranscriptToken,
    disableAutoscroll,
    resumeAutoscroll,
    seek,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Transcript);



// WEBPACK FOOTER //
// ./scripts/components/transcript/transcript.js